import { faCalendarDay, faCircleCheck, faCircleXmark, faSend } from "@fortawesome/pro-solid-svg-icons"
import { Grid, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"
import { Box } from "@mui/system"
import ClpButton from "components/Button"
import moment from "moment"
import EmailBoxInput from "../EmailBoxInput"
import EmailBoxTypography from "../EmailBoxTypography"
import { statusButtonColor, statusButtonText, statusSubText } from "../helpers"
import {
  StyledEmailBox,
  StyledErrorFontAwesomeIcon,
  StyledErrorTypography,
  StyledPrimaryFontAwesomeIcon,
  StyledPrimaryTypography,
  StyledSendFontAwesomeIcon,
  StyledSuccessFontAwesomeIcon,
  StyledSuccessTypograpy
} from "./WorkOrderEmailBox.styled"
import { useTranslation } from "react-i18next"
import { fontAwesomeOldXlStyle } from "components/icons/fontAwesomeStyles"

const getStatusStyles = (status, theme) => {
  switch (status) {
    case 4:
      return {
        backgroundColor: theme.palette.primary["light"]
      }
    case 5:
      return {
        backgroundColor: theme.palette.success["light"]
      }
    case 6:
      return {
        backgroundColor: theme.palette.error["light"]
      }
    default:
      return {
        backgroundColor: theme.palette.primary["light"]
      }
  }
}

const statusIcon = (value) => {
  switch (value) {
    case 1:
      return null
    case 2:
      return <StyledPrimaryFontAwesomeIcon icon={faSend} style={fontAwesomeOldXlStyle} />
    case 3:
      return <StyledPrimaryFontAwesomeIcon icon={faCalendarDay} style={fontAwesomeOldXlStyle} />
    case 4:
      return <StyledPrimaryFontAwesomeIcon icon={faCircleCheck} style={fontAwesomeOldXlStyle} />
    case 5:
      return <StyledSuccessFontAwesomeIcon icon={faCircleCheck} style={fontAwesomeOldXlStyle} />
    case 6:
      return <StyledErrorFontAwesomeIcon icon={faCircleXmark} style={fontAwesomeOldXlStyle} />
    default:
      return null
  }
}

const statusText = (value, t) => {
  switch (value) {
    case 1:
      return (
        <StyledPrimaryTypography variant="h2">
          {t("global.send_booking", "Send booking")}
        </StyledPrimaryTypography>
      )
    case 2:
      return <StyledPrimaryTypography variant="h2">{t("global.send", "Send")}</StyledPrimaryTypography>
    case 3:
      return <StyledPrimaryTypography variant="h2">{t("global.booked", "Booked")}</StyledPrimaryTypography>
    case 4:
      return <StyledPrimaryTypography variant="h2">{t("global.fixed", "Fixed")}</StyledPrimaryTypography>
    case 5:
      return (
        <StyledSuccessTypograpy variant="h2">{t("global.completed", "Completed")}</StyledSuccessTypograpy>
      )
    case 6:
      return <StyledErrorTypography variant="h2">{t("global.cancelled", "Cancelled")}</StyledErrorTypography>
    default:
      return (
        <StyledPrimaryTypography variant="h2">
          {t("global.send_booking", "Send booking")}
        </StyledPrimaryTypography>
      )
  }
}

const StyledBox = styled(StyledEmailBox)(({ theme, status }) => ({
  ...getStatusStyles(status, theme)
}))

const WorkOrderEmailBox = ({ values, handleChange, handleBlur, touched, errors, submit, uploadedFiles }) => {
  const { t } = useTranslation()
  const statusContent = (value) => {
    switch (value) {
      case 1:
        return (
          <Box>
            <EmailBoxTypography pb={1} text={t("global.email", "Email")} variant="h6" />
            <EmailBoxInput
              t={t}
              values={values}
              handleBlur={handleBlur}
              handleChange={handleChange}
              touched={touched}
              errors={errors}
            />
          </Box>
        )
      case 2:
        return (
          <Box>
            <EmailBoxTypography pt={2} text={t("global.receiver", "Receiver")} variant="bodySmall" />
            <EmailBoxTypography text={values.emailRecipient ? values.emailRecipient : "-"} variant="h6" />
          </Box>
        )
      case 3:
        return (
          <Box>
            <EmailBoxTypography text={t("global.planned_action", "Planned action")} variant="bodySmall" />
            <EmailBoxTypography
              text={
                moment(values.plannedDate).isValid()
                  ? moment.utc(values.plannedDate).local().format("YYYY-MM-DD HH:mm")
                  : "-"
              }
              variant="h6"
            />
            <EmailBoxTypography pt={2} text={t("global.receiver", "Receiver")} variant="bodySmall" />
            <EmailBoxTypography text={values.emailRecipient ? values.emailRecipient : "-"} variant="h6" />
          </Box>
        )
      case 4:
        return (
          <Box>
            <EmailBoxTypography text={t("global.fixed", "Fixed")} variant="bodySmall" />
            <EmailBoxTypography
              text={
                moment(values.resolvedDate).isValid()
                  ? moment.utc(values.resolvedDate).local().format("YYYY-MM-DD HH:mm")
                  : "-"
              }
              variant="h6"
            />
            <EmailBoxTypography pt={2} text={t("global.receiver", "Receiver")} variant="bodySmall" />
            <EmailBoxTypography text={values.emailRecipient ? values.emailRecipient : "-"} variant="h6" />
          </Box>
        )
      case 5:
        return (
          <Box>
            <EmailBoxTypography text={t("global.fixed", "Fixed")} variant="bodySmall" />
            <EmailBoxTypography
              text={
                moment(values.resolvedDate).isValid()
                  ? moment.utc(values.resolvedDate).local().format("YYYY-MM-DD HH:mm")
                  : "-"
              }
              variant="h6"
            />
            <EmailBoxTypography pt={2} text={t("global.receiver", "Receiver")} variant="bodySmall" />
            <EmailBoxTypography text={values.emailRecipient ? values.emailRecipient : "-"} variant="h6" />
          </Box>
        )
      case 6:
        return (
          <Box mb={2}>
            <EmailBoxTypography pt={2} text={t("global.receiver", "Receiver")} variant="bodySmall" />
            <EmailBoxTypography text={values.emailRecipient ? values.emailRecipient : "-"} variant="h6" />
          </Box>
        )

      default:
        return (
          <Box>
            <EmailBoxTypography pb={1} text={t("global.email", "Email")} variant="h6" />
            <EmailBoxInput
              t={t}
              values={values}
              handleBlur={handleBlur}
              handleChange={handleChange}
              touched={touched}
              errors={errors}
            />
          </Box>
        )
    }
  }

  return (
    <>
      <Grid item xs={12} md={12} lg={4} pl={2} direction="row" justifyContent="space-between">
        <StyledBox status={values.status}>
          <Box pt={4} pb={4} pl={3} pr={3}>
            <Grid container direction="row" alignItems="center" mb={3}>
              {statusIcon(values.status)}
              {statusText(values.status, t)}
            </Grid>
            {statusContent(values.status)}

            {values.status < 5 && (
              <Box mt={3} mb={2}>
                <ClpButton
                  color={statusButtonColor(values.status)}
                  variant="contained"
                  type="button"
                  onClick={() => submit(true)}
                  endIcon={values.status < 2 ? <StyledSendFontAwesomeIcon icon={faSend} /> : null}>
                  {statusButtonText(values.status, t)}
                </ClpButton>
              </Box>
            )}

            {values.status !== 5 && (
              <Typography variant="bodySmall">{statusSubText(values.status, t)}</Typography>
            )}
          </Box>
        </StyledBox>
      </Grid>
    </>
  )
}

export default WorkOrderEmailBox
